import { motion } from 'framer-motion';
import { REGIONS, UPTIME_30D } from './data';

// STATUS — public uptime grid + per-region table. NOT a marketing page.
const SERVICES = [
  { name: 'edge runtime', uptime: '99.997%', state: 'OPERATIONAL' },
  { name: 'job queue', uptime: '99.991%', state: 'OPERATIONAL' },
  { name: 'vault', uptime: '100.000%', state: 'OPERATIONAL' },
  { name: 'observability', uptime: '99.984%', state: 'DEGRADED' },
  { name: 'control plane', uptime: '99.999%', state: 'OPERATIONAL' },
  { name: 'api gateway', uptime: '99.996%', state: 'OPERATIONAL' },
];

const INCIDENTS = [
  { date: 'Feb 04 · 14:22 UTC', sev: 'minor', region: 'eu-central-1', t: 'Cold-start latency above SLO for 11 min', resolved: 'resolved · post-mortem #248' },
  { date: 'Jan 22 · 09:14 UTC', sev: 'minor', region: 'ap-southeast-1', t: 'Job queue · cursor lag of 38s', resolved: 'resolved · post-mortem #247' },
  { date: 'Dec 18 · 02:08 UTC', sev: 'major', region: 'us-east-1', t: 'API gateway 503s for 4 min during DB failover', resolved: 'resolved · post-mortem #244' },
];

export default function Status() {
  return (
    <div className="bg-[#0A0E14] text-[#E6EDF3] font-mono min-h-screen">
      <section className="pt-32 pb-8 max-w-[1500px] mx-auto px-5 lg:px-8 border-b border-[#1A2230]">
        <p className="text-[10px] tracking-[0.32em] text-[#06D6A0] uppercase mb-3">// public status · ping</p>
        <div className="grid lg:grid-cols-[1fr_auto] items-end gap-6">
          <h1 className="font-sans text-5xl lg:text-7xl tracking-[-0.025em] lowercase leading-[0.98] font-medium">all systems<br /><span className="text-[#06D6A0]">green-ish.</span></h1>
          <div className="text-right">
            <p className="text-[10px] tracking-[0.3em] text-[#5F7184] uppercase">global uptime · 30d</p>
            <p className="text-3xl text-[#06D6A0]">99.992%</p>
            <p className="text-[10px] text-[#5F7184] mt-0.5">last refreshed 14s ago</p>
          </div>
        </div>
      </section>

      {/* SERVICE GRID — each row is a service with 30-day uptime block grid */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-8 py-12 space-y-3">
        {SERVICES.map((s, i) => (
          <motion.article key={s.name} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }} className="border border-[#1A2230] bg-[#0E141C] rounded-sm grid grid-cols-1 lg:grid-cols-[200px_1fr_140px_140px] items-center gap-4 lg:gap-6 p-4 lg:p-5">
            <div>
              <p className="text-base text-[#E6EDF3] lowercase">{s.name}</p>
              <p className="text-[10px] text-[#5F7184] mt-0.5">// last 30 days</p>
            </div>
            <div className="grid grid-cols-30 gap-[3px]" style={{ gridTemplateColumns: 'repeat(30, 1fr)' }}>
              {UPTIME_30D.map((day, idx) => (
                <div key={idx} className={`h-7 rounded-[1px] ${day === 'O' ? 'bg-[#06D6A0]/85 hover:bg-[#06D6A0]' : 'bg-[#FFB454]/85 hover:bg-[#FFB454]'} transition-colors`} title={`Day ${idx + 1}: ${day === 'O' ? '100% up' : 'partial degradation'}`} />
              ))}
            </div>
            <p className="text-sm text-[#E6EDF3]">{s.uptime}</p>
            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 inline-block w-fit ${s.state === 'OPERATIONAL' ? 'text-[#06D6A0] border border-[#06D6A0]/30 bg-[#06D6A0]/10' : 'text-[#FFB454] border border-[#FFB454]/30 bg-[#FFB454]/10'}`}>{s.state}</span>
          </motion.article>
        ))}
      </section>

      {/* REGIONS + INCIDENTS */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-2 gap-10">
        <div className="border border-[#1A2230] bg-[#0E141C] rounded-sm">
          <div className="px-5 py-3 border-b border-[#1A2230] flex items-center justify-between">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5F7184]">// regions</p>
            <p className="text-[10px] text-[#06D6A0]">● {REGIONS.filter(r => r.state === 'OPERATIONAL').length}/{REGIONS.length} live</p>
          </div>
          <div className="divide-y divide-[#1A2230]">
            {REGIONS.map(r => (
              <div key={r.code} className="px-5 py-3 grid grid-cols-[1fr_70px_120px] items-center gap-3 text-xs">
                <div>
                  <p className="text-[#E6EDF3]">{r.code}</p>
                  <p className="text-[10px] text-[#5F7184] mt-0.5">{r.city}</p>
                </div>
                <p className="text-[#5F7184] text-right">{r.latency}</p>
                <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 text-right ${r.state === 'OPERATIONAL' ? 'text-[#06D6A0]' : 'text-[#FFB454]'}`}>{r.state}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-[#1A2230] bg-[#0E141C] rounded-sm">
          <div className="px-5 py-3 border-b border-[#1A2230]">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5F7184]">// incident history · last 90 days</p>
          </div>
          <div className="divide-y divide-[#1A2230]">
            {INCIDENTS.map(inc => (
              <article key={inc.date} className="px-5 py-4">
                <div className="flex items-baseline justify-between mb-1.5">
                  <p className="text-[10px] text-[#5F7184]">{inc.date}</p>
                  <span className={`text-[9px] tracking-[0.2em] uppercase ${inc.sev === 'major' ? 'text-[#FF7B72]' : 'text-[#FFB454]'}`}>{inc.sev} · {inc.region}</span>
                </div>
                <p className="font-sans text-sm text-[#E6EDF3]">{inc.t}</p>
                <p className="text-[10px] text-[#06D6A0] mt-1.5">✓ {inc.resolved}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
